import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Box,
    Checkbox,
    Divider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { ChangeEvent, useContext, useState } from "react";
import { AccountService, OtpType } from "../../../api/psc";
import {
    ConfirmationDialog,
    DialogContainer,
    DialogHeader,
} from "../../../components/exports";
import {
    AccountResetConfigurationList,
    AccountResetConfigurationTitle,
} from "../../../constants/ProjectConstants";
import { DialogContext } from "../../../contexts/DialogueContext";

export default function AccountResetDialog() {
    const [configuration, setConfiguration] = useState(0);
    const [otpCode, setOtpCode] = useState("");
    const [verificationCodeSent, setVerificationCodeSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [requestInProgress, setRequestInProgress] = useState(false);

    const { openDialog, closeDialog } = useContext(DialogContext);

    const toggleConfiguration = (p: number) => {
        const updatedConfiguration =
            (configuration & p) === 0 ? configuration + p : configuration - p;
        setConfiguration(updatedConfiguration);
    };

    const sendVerificationCode = () => {
        setRequestInProgress(true);
        setError(null);
        AccountService.sendOtp("1", {
            otpType: OtpType.RESET_ACCOUNT,
        })
            .then((r) => {
                setVerificationCodeSent(true);
            })
            .finally(() => {
                setRequestInProgress(false);
            });
    };

    const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOtpCode(event.target.value);
    };

    const confirmResetAccount = () => {
        openDialog(
            <ConfirmationDialog
                title="توجه داشته باشید اطلاعات حذف شده به هیچ عنوان قابل بازگرداندن نخواهد بود!"
                onAccept={applyResetAccount}
            />
        );
    };

    const applyResetAccount = () => {
        setRequestInProgress(true);
        AccountService.resetAccount("1", {
            configuration: configuration,
            verificationCode: otpCode,
        })
            .then((r) => {
                closeDialog();
            })
            .catch((e) => {
                setError("کد تایید وارد شده صحیح نیست");
                setVerificationCodeSent(false);
            })
            .finally(() => {
                setRequestInProgress(false);
            });
    };

    return (
        <DialogContainer>
            <DialogHeader title="حذف انتخابی اطلاعات" />

            <Stack sx={{ p: 2 }} spacing={2}>
                <Typography>
                    با انتخاب هر یک از موارد زیر اطلاعات مربوطه از حساب کاربری
                    شما برای همیشه حذف خواهد شد، لطفا در صورت عدم اطمینان از
                    کارکرد این بخش با پشتیبانی تماس حاصل نمایید.
                </Typography>

                <Divider />

                <Alert severity="warning">
                    توجه: بعد از اعمال حذف انتخابی اطلاعات به هیچ عنوان امکان
                    برگرداندن اطلاعات حذف شده وجود نخواهد داشت!
                </Alert>

                <Divider />

                <Stack>
                    {AccountResetConfigurationList.map((p) => {
                        return (
                            <Box
                                key={p}
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <Checkbox
                                    checked={
                                        (configuration & p.valueOf()) !== 0
                                    }
                                    disabled={verificationCodeSent}
                                    onChange={() => toggleConfiguration(p)}
                                />
                                <Typography>
                                    {AccountResetConfigurationTitle(p)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>

                {error && <Alert severity="error">{error}</Alert>}

                {!verificationCodeSent && configuration > 0 && (
                    <LoadingButton
                        variant="contained"
                        loading={requestInProgress}
                        fullWidth
                        onClick={sendVerificationCode}
                    >
                        ارسال کد تایید
                    </LoadingButton>
                )}

                {verificationCodeSent && (
                    <>
                        <TextField
                            type="tel"
                            onChange={handleCodeChange}
                            autoComplete="one-time-code"
                            fullWidth
                            autoFocus
                            label="کد یکبار مصرف"
                        />

                        <LoadingButton
                            variant="contained"
                            loading={requestInProgress}
                            color="warning"
                            onClick={confirmResetAccount}
                        >
                            تایید و حذف اطلاعات انتخابی
                        </LoadingButton>
                    </>
                )}
            </Stack>
        </DialogContainer>
    );
}
